
import React, { useState, useEffect } from 'react';
import { Search, FileText, ChevronRight, Copy, CheckCircle2, History, Mic, Trash2 } from 'lucide-react';

interface TranscriptionsProps {
  recordings: any[];
  onRecordingsChange: (recordings: any[]) => void;
  onDeleteTranscription: (recordingId: string) => void;
  initialRecordingId?: string | null;
}

const Transcriptions: React.FC<TranscriptionsProps> = ({ recordings, onDeleteTranscription, initialRecordingId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTranscription, setSelectedTranscription] = useState<any | null>(() => {
    if (initialRecordingId) {
      return recordings.find(r => r.id === initialRecordingId && r.transcription) || null;
    }
    return null;
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (initialRecordingId) {
      const found = recordings.find(r => r.id === initialRecordingId && r.transcription);
      if (found) {
        setSelectedTranscription(found);
      }
    }
  }, [initialRecordingId, recordings]);

  const filtered = recordings.filter(r => 
    r.transcription && 
    (r.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
     r.patientName.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black dark:text-white tracking-tight">Transcrições</h1>
          <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mt-1">Textos literais processados pela Supervisão IA.</p>
        </div>
        <div className="relative w-full md:w-80 group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={20} />
          <input 
            type="text" 
            placeholder="Buscar por paciente ou sessão..." 
            className="w-full pl-12 pr-4 py-3.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl text-sm shadow-sm focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-400 outline-none transition-all dark:text-slate-100"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-10 items-start">
        {/* Lista Lateral */}
        <div className="lg:col-span-2 space-y-4 max-h-[calc(100vh-280px)] overflow-y-auto pr-3 custom-scrollbar">
          {filtered.map(rec => (
            <div 
              key={rec.id}
              className={`w-full p-7 rounded-[2.5rem] border-2 text-left transition-all relative group/item cursor-pointer shadow-sm ${selectedTranscription?.id === rec.id ? 'bg-indigo-600 border-indigo-600 text-white shadow-xl scale-[1.02] z-10' : 'bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:border-indigo-300'}`}
              onClick={() => setSelectedTranscription(rec)}
            >
              <div className="flex items-center gap-4 mb-4">
                <div className={`p-3 rounded-2xl ${selectedTranscription?.id === rec.id ? 'bg-white/20' : 'bg-slate-100 dark:bg-slate-800'}`}>
                  <Mic size={18} />
                </div>
                <div className="flex-1 overflow-hidden">
                  <h4 className="text-[15px] font-black truncate leading-tight tracking-tight">{rec.name}</h4>
                  <p className={`text-[10px] font-black uppercase tracking-widest mt-0.5 ${selectedTranscription?.id === rec.id ? 'text-indigo-100' : 'text-slate-400'}`}>{rec.patientName}</p>
                </div>
              </div>
              <p className={`text-[11px] italic line-clamp-2 mt-3 leading-relaxed opacity-80 font-medium`}>
                {rec.transcription}
              </p>
              
              <button 
                onClick={(e) => { e.stopPropagation(); onDeleteTranscription(rec.id); if(selectedTranscription?.id === rec.id) setSelectedTranscription(null); }}
                className={`absolute top-5 right-5 p-2.5 rounded-xl opacity-0 group-hover/item:opacity-100 transition-all ${selectedTranscription?.id === rec.id ? 'hover:bg-white/20 text-white' : 'hover:bg-red-50 text-slate-400 hover:text-red-500'}`}
                title="Excluir Transcrição"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="p-10 sm:p-20 text-center bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] border border-dashed border-slate-200 dark:border-slate-800 shadow-sm">
               <History size={64} className="mx-auto mb-6 text-slate-300 dark:text-slate-700 opacity-20" />
               <p className="text-sm font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest tracking-tighter">Nenhuma transcrição encontrada</p>
            </div>
          )}
        </div>

        {/* Visualização de Conteúdo */}
        <div className="lg:col-span-3 h-full">
          {selectedTranscription ? (
            <div className="bg-white dark:bg-slate-900 p-6 sm:p-12 rounded-[2rem] sm:rounded-[3.5rem] border border-slate-200 dark:border-slate-800 shadow-sm animate-in zoom-in-95 duration-500 h-full min-h-[550px] flex flex-col">
               <div className="flex justify-between items-center mb-10 border-b dark:border-slate-800 pb-10">
                  <div>
                    <h3 className="text-3xl font-black dark:text-white uppercase tracking-tighter tracking-tight">{selectedTranscription.patientName}</h3>
                    <div className="flex items-center gap-3 mt-3">
                      <span className="text-[11px] text-indigo-600 dark:text-indigo-400 font-black uppercase tracking-[0.2em] bg-indigo-50 dark:bg-indigo-900/30 px-4 py-1.5 rounded-full border border-indigo-100 dark:border-indigo-800">{selectedTranscription.name}</span>
                      <span className="text-[11px] text-slate-400 font-black uppercase tracking-[0.2em]">{selectedTranscription.date}</span>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => handleCopy(selectedTranscription.transcription)}
                      className="flex items-center gap-2.5 px-6 py-3 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-indigo-600 hover:text-white transition-all border border-slate-200 dark:border-slate-700"
                    >
                      {copied ? <CheckCircle2 size={16} className="text-emerald-500" /> : <Copy size={16} />}
                      {copied ? "Texto Copiado!" : "Copiar Transcrição"}
                    </button>
                  </div>
               </div>
               <div className="flex-1 text-[15px] leading-relaxed text-slate-700 dark:text-slate-300 font-medium whitespace-pre-wrap selection:bg-indigo-100 overflow-y-auto custom-scrollbar pr-6">
                  {selectedTranscription.transcription}
               </div>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 border-2 border-dashed border-slate-200 dark:border-slate-800 p-32 rounded-[2rem] sm:rounded-[3.5rem] flex flex-col items-center justify-center text-center h-full">
               <div className="w-24 h-24 bg-slate-100 dark:bg-slate-800 rounded-[2.5rem] flex items-center justify-center mb-8 shadow-sm text-slate-300"><FileText size={48} /></div>
               <h3 className="text-2xl font-black text-slate-400 dark:text-slate-700 uppercase tracking-widest tracking-tighter">Selecione uma transcrição</h3>
               <p className="text-sm text-slate-400 mt-4 max-w-[320px] font-medium leading-relaxed italic">Clique em uma sessão na lista ao lado para visualizar a transcrição literal completa.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transcriptions;
